export class Session {
  constructor(id, userIp, userAgent, userCountry, createdAt, usedAt, isCurrent = false, device = null, browser = null) {
    this.id = id;
    this.userIp = userIp;
    this.userAgent = userAgent;
    this.userCountry = userCountry;
    this.createdAt = createdAt;
    this.usedAt = usedAt;
    this.isCurrent = isCurrent;
    this.device = device;
    this.browser = browser;
  }

  // Factory method to create Session from API data
  static create(rawSession, currentSessionId = null) {
    const {
      id,
      user_ip,
      user_agent,
      user_country,
      created_at,
      used_at,
      device,
      browser,
    } = rawSession;

    const isCurrent = currentSessionId ? id === currentSessionId : false;

    return new Session(
      id,
      user_ip || 'Unknown IP',
      user_agent || 'Unknown Device',
      user_country || 'Unknown Location',
      created_at,
      used_at,
      isCurrent,
      device || null,
      browser || null,
    );
  }

  // Batch create sessions
  static createMany(rawSessions, currentSessionId = null) {
    return rawSessions.map(session => Session.create(session, currentSessionId));
  }

  // Get formatted browser info
  getBrowserInfo() {
    return this.browser || 'Unknown Browser';
  }

  // Get OS display name derived from device type
  getOsInfo() {
    const map = { windows: 'Windows', apple: 'macOS / iOS', android: 'Android', linux: 'Linux' };
    return map[this.device] || 'Unknown OS';
  }

  // Get created date
  getCreatedDate() {
    return this.createdAt ? new Date(this.createdAt) : null;
  }

  // Get last used date
  getUsedDate() {
    return this.usedAt ? new Date(this.usedAt) : null;
  }

  // Get formatted first login string
  getFormattedFirstLogin() {
    const date = this.getCreatedDate();
    if (!date) return 'Unknown';
    
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else if (diffInDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
  }

  // Get time ago string for last used date
  getTimeAgo() {
    const date = this.getUsedDate() || this.getCreatedDate();
    if (!date) return 'Unknown';
    
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes < 1440) { // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  }
}

export default Session;
